import { Form, FormInstance, InputNumber } from "antd";
import { DiffRow } from "pages/settings/policy/PolicySettingsPage";
import { PolicySection } from "pages/settings/policy/policySection";
import { SettingRow, SettingsSection } from "pages/settings/settingsRows";
import { translate } from "shared/languages";
import { formatDate, now } from "shared/utils/dayjs";

const KEY = "visibility";

type Values = {
  schedule_visibility_days: number;
};

// A sanity bound, matching the server parser. Not a recommendation: both the
// client and instructor schedule feeds clip their forward end to this setting,
// and the instructor feed's booking match gets slow well before a year.
const MAX_VISIBILITY_DAYS = 365;

const Fields = ({ form }: { form: FormInstance }) => {
  // A day count is hard to picture, so resolve it to the date it actually
  // reaches. Mirrors the server's now().add(days, "day") in Jakarta.
  const days = Form.useWatch("schedule_visibility_days", form);
  const helper =
    typeof days === "number" && days >= 1 && days <= MAX_VISIBILITY_DAYS
      ? translate("class_settings.visibility.helper").replace(
          "{date}",
          formatDate(now().add(days, "day")),
        )
      : undefined;

  return (
    <SettingsSection
      id={KEY}
      title={translate("class_settings.visibility.title")}
      description={translate("class_settings.visibility.subtitle")}
    >
      <SettingRow
        last
        title={translate("class_settings.visibility.days")}
        hint={helper}
        control={
          <Form.Item
            name="schedule_visibility_days"
            style={{ margin: 0 }}
            rules={[
              { required: true, message: translate("form.error.required") },
              // Typing past the cap can't reach this: InputNumber's own max
              // clamps first. It is here for the one path that bypasses the
              // clamp, a business whose STORED value predates the cap.
              // Hydration doesn't clamp, so without this the admin would get
              // an opaque server 400 instead of a clear message.
              {
                type: "integer",
                min: 1,
                max: MAX_VISIBILITY_DAYS,
                message: translate("class_settings.visibility.range_error"),
              },
            ]}
          >
            <InputNumber
              min={1}
              max={MAX_VISIBILITY_DAYS}
              precision={0}
              style={{ width: 130 }}
              addonAfter={translate("class_settings.unit.days")}
              aria-label={translate("class_settings.visibility.days")}
            />
          </Form.Item>
        }
      />
    </SettingsSection>
  );
};

export const visibilityPolicy: PolicySection = {
  key: KEY,
  initialValues: (s) => ({
    schedule_visibility_days: s.schedule_visibility_days,
  }),
  buildSave: (v: Values, s) => {
    const days = Number(v.schedule_visibility_days);
    const unit = translate("class_settings.unit.days");
    const rows: DiffRow[] =
      days === s.schedule_visibility_days
        ? []
        : [
            {
              label: translate("class_settings.visibility.days"),
              before: `${s.schedule_visibility_days} ${unit}`,
              after: `${days} ${unit}`,
            },
          ];
    return {
      rows,
      payload: { schedule_visibility_days: days },
      note: translate("class_settings.confirm.note_live"),
    };
  },
  Fields,
};
